import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spin, Table, Modal as AntdModal } from "antd";
import { Spinner } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";
import { FiSearch, FiCalendar, FiX, FiUsers, FiDownload, FiChevronDown, FiList, FiFileText, FiPercent, FiCreditCard, FiTrendingUp, FiBriefcase, FiPhone } from "react-icons/fi";
import SidebarNav from "../../sidebar";
import FilterPanel from "../../commoncomponent/FilterPanel";
import {
  getTdsMonthlyApi,
  exportTdsAnnualSummaryApi,
  downloadTdsMonthlyDetailApi,
  getTdsUsersApi,
  exportTdsUsersApi,
} from "../../../../service/api";
import { formatInr, formatNumber, KpiCard, saveBlobAsFile } from "../shared";
import { sanitizeInput } from "../../../../config/validation";

const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const AVATAR_COLORS = ["#6f42c1", "#1b5a90", "#28a745", "#fd7e14", "#e83e8c", "#20c997", "#dc3545"];
const getAvatarColor = (name) => {
  if (!name) return "#8a94a6";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const getInitials = (name) => {
  if (!name) return "?";
  const p = name.trim().split(" ").filter(Boolean);
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

const TDSReports = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingMonth, setDownloadingMonth] = useState(null);

  // Per-user TDS breakdown — separate fetch with its own filters.
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotals, setUsersTotals] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(25);
  const [usersMonth, setUsersMonth] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  // debounced version of search so we don't spam the API on each keystroke
  const [usersSearchDebounced, setUsersSearchDebounced] = useState("");
  const [exportingUsers, setExportingUsers] = useState(false);

  // 400ms debounce
  useEffect(() => {
    const t = setTimeout(
      () => setUsersSearchDebounced(usersSearch.trim()),
      400,
    );
    return () => clearTimeout(t);
  }, [usersSearch]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getTdsMonthlyApi({ year });
      if (res?.status) setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const userParams = useMemo(() => {
    const p = { year, page: usersPage, sizePerPage: usersPageSize };
    if (usersMonth) p.month = usersMonth;
    if (usersSearchDebounced) p.search = usersSearchDebounced;
    return p;
  }, [year, usersPage, usersPageSize, usersMonth, usersSearchDebounced]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await getTdsUsersApi(userParams);
      if (res?.status) {
        const d = res?.data || {};
        const list = Array.isArray(d?.result) ? d.result : [];
        setUsers(list);
        setUsersTotal(Number(d?.total) || 0);
        setUsersTotals(d?.totals || null);
      } else {
        setUsers([]);
        setUsersTotal(0);
        setUsersTotals(null);
      }
    } catch (e) {
      console.error(e);
      setUsers([]);
      setUsersTotal(0);
      setUsersTotals(null);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setUsersPage(1);
  }, [year, usersMonth, usersSearchDebounced, usersPageSize]);

  const handleUsersExport = async () => {
    setExportingUsers(true);
    try {
      const exportParams = { year };
      if (usersMonth) exportParams.month = usersMonth;
      if (usersSearchDebounced) exportParams.search = usersSearchDebounced;
      const res = await exportTdsUsersApi(exportParams);
      const monthPart = usersMonth ? `-${String(usersMonth).padStart(2, "0")}` : "";
      saveBlobAsFile(res, `tds-users-${year}${monthPart}.csv`);
      toast.success("Download started", { toastId: 'download-started' });
    } catch (e) {
      toast.error("Export failed", { toastId: 'download-failed' });
    } finally {
      setExportingUsers(false);
    }
  };

  const handleAnnualExport = async () => {
    setExporting(true);
    try {
      const res = await exportTdsAnnualSummaryApi({ year });
      saveBlobAsFile(res, `tds-annual-summary-${year}.csv`);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const handleMonthDownload = (month) => {
    AntdModal.confirm({
      title: "Download per-trade TDS report",
      content:
        "This file contains personally identifiable information (PAN). Handle and store it according to your data-protection policy.",
      okText: "Download",
      cancelText: "Cancel",
      onOk: async () => {
        setDownloadingMonth(month);
        try {
          const res = await downloadTdsMonthlyDetailApi({ year, month });
          saveBlobAsFile(res, `tds-${year}-${String(month).padStart(2, "0")}.csv`);
          toast.success("Download started", { toastId: 'download-started' });
        } catch (e) {
          toast.error("Download failed", { toastId: 'download-failed' });
        } finally {
          setDownloadingMonth(null);
        }
      },
    });
  };

  const barChart = useMemo(() => {
    const rows = data?.rows || [];
    return {
      options: {
        chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
        plotOptions: { bar: { columnWidth: "50%", borderRadius: 6 } },
        xaxis: {
          categories: rows.map((r) => r.label),
          labels: { style: { colors: "#8a94a6", fontSize: "12px" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: { style: { colors: "#8a94a6", fontSize: "12px" }, formatter: (v) => formatInr(v) },
        },
        grid: { borderColor: "#f0f0f0", strokeDashArray: 4 },
        colors: ["#6f42c1"],
        tooltip: { y: { formatter: (v) => formatInr(v) } },
        dataLabels: { enabled: false },
        fill: { type: "gradient", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.2, opacityFrom: 1, opacityTo: 0.85 } },
      },
      series: [{ name: "TDS Collected", data: rows.map((r) => Number(r.tdsCollected || 0)) }],
    };
  }, [data]);

  const columns = [
    {
      title: "Month",
      dataIndex: "label",
      key: "label",
      render: (v) => <span style={{ fontWeight: 700, color: "#1a1a2e" }}>{v}</span>,
    },
    {
      title: "TDS Collected",
      key: "tdsCollected",
      align: "right",
      render: (_, r) => <span style={{ color: "#6f42c1", fontWeight: 700 }}>{formatInr(r.tdsCollected)}</span>,
    },
    {
      title: "TDS-eligible Volume",
      key: "volume",
      align: "right",
      render: (_, r) => <span style={{ color: "#1b5a90", fontWeight: 600 }}>{formatInr(r.tradingVolumeSubjectToTds)}</span>,
    },
    {
      title: "Transactions",
      key: "transactionCount",
      align: "right",
      render: (_, r) => <span style={{ color: "#0dcaf0", fontWeight: 600 }}>{formatNumber(r.transactionCount)}</span>,
    },
    {
      title: "",
      key: "action",
      align: "center",
      render: (_, r) => (
        <button
          onClick={() => handleMonthDownload(r.month)}
          disabled={downloadingMonth === r.month}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#f4eefb", border: "1.5px solid #6f42c133", borderRadius: 8, color: "#6f42c1", fontWeight: 600, fontSize: 12, cursor: "pointer", opacity: downloadingMonth === r.month ? 0.6 : 1 }}
        >
          {downloadingMonth === r.month ? <Spinner size="sm" /> : <><FiDownload size={13} /> Download</>}
        </button>
      ),
    },
  ];

  // Per-user table — columns match the actual response shape:
  // { userId, name, email, phoneNumber, pan,
  //   trading: { tds, gst, fee, volume, tradeCount },
  //   withdrawal: { tds, gst, fee, count },
  //   totalTds, totalGst, totalFee }
  const userColumns = [
    {
      title: "User",
      key: "user",
      render: (_, r) => {
        const name = r?.name || "";
        const color = getAvatarColor(name);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>{getInitials(name)}</span>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e" }}>
                {r?.userId ? (
                  <Link to={`/admin/user/profile/${r.userId}`} style={{ color: "inherit", textDecoration: "none" }}>{name || "-"}</Link>
                ) : (name || "-")}
              </div>
              <div style={{ fontSize: 11, color: "#8a94a6" }}>{r?.email || "-"}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "PAN",
      key: "pan",
      render: (_, r) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r?.pan || "-"}</span>,
    },
    {
      title: "Phone",
      key: "phone",
      render: (_, r) => r?.phoneNumber ? (
        <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#374151", fontSize: 13 }}>
          <FiPhone size={12} color="#1b5a90" />
          {r.phoneNumber}
        </div>
      ) : "-",
    },
    {
      title: "Trading TDS",
      key: "tradingTds",
      align: "right",
      render: (_, r) => formatInr(r?.trading?.tds || 0),
    },
    {
      title: "Trading Volume",
      key: "tradingVolume",
      align: "right",
      render: (_, r) => formatInr(r?.trading?.volume || 0),
    },
    {
      title: "Trades",
      key: "tradeCount",
      align: "right",
      render: (_, r) => formatNumber(r?.trading?.tradeCount || 0),
    },
    {
      title: "Withdrawal TDS",
      key: "withdrawalTds",
      align: "right",
      render: (_, r) => formatInr(r?.withdrawal?.tds || 0),
    },
    {
      title: "Withdrawals",
      key: "withdrawalCount",
      align: "right",
      render: (_, r) => formatNumber(r?.withdrawal?.count || 0),
    },
    {
      title: "Total TDS",
      key: "totalTds",
      align: "right",
      render: (_, r) => (
        <span style={{ fontWeight: 600 }}>{formatInr(r?.totalTds || 0)}</span>
      ),
    },
    {
      title: "Total GST",
      key: "totalGst",
      align: "right",
      render: (_, r) => formatInr(r?.totalGst || 0),
    },
    {
      title: "Total Fee",
      key: "totalFee",
      align: "right",
      render: (_, r) => formatInr(r?.totalFee || 0),
    },
  ];

  const totals = data?.totals || {
    tdsCollected: 0,
    tradingVolumeSubjectToTds: 0,
    avgMonthlyTds: 0,
    monthsFiled: 0,
    transactionCount: 0,
  };

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header py-3 mb-2">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-1">TDS Reports (Trading)</h3>
                <ul className="breadcrumb mb-0" style={{ fontSize: 13 }}>
                  <li className="breadcrumb-item">
                    <Link to="/admin" className="text-decoration-none">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Finance Dashboard / TDS Reports</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="row g-3 mb-3">
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="T" label={`TDS Collected ${year}`} value={formatInr(totals.tdsCollected)} color="#6f42c1" highlight />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="V" label="TDS-eligible Volume" value={formatInr(totals.tradingVolumeSubjectToTds)} color="#1b5a90" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="Ø" label="Avg Monthly TDS" value={formatInr(totals.avgMonthlyTds)} color="#fd7e14" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="#" label="Months Filed" value={`${formatNumber(totals.monthsFiled)} / 12`} color="#28a745" />
                </div>
              </div>

              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>TDS Collected per Month</h6>
                      <div style={{ fontSize: 12, color: "#8a94a6", marginTop: 2 }}>Monthly TDS breakdown for {year}</div>
                    </div>
                    <div style={{ background: "#f4eefb", border: "1px solid #6f42c122", borderRadius: 10, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <FiFileText size={14} color="#6f42c1" />
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total TDS</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: "#6f42c1" }}>{formatInr(totals.tdsCollected)}</div>
                      </div>
                    </div>
                  </div>
                  {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <ReactApexChart options={barChart.options} series={barChart.series} type="bar" height={320} />
                  )}
                </div>
              </div>

              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginTop: 16 }}>
                <div className="card-body">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>Monthly Summary</h6>
                      <div style={{ fontSize: 12, color: "#8a94a6", marginTop: 2 }}>
                        {data?.rows?.length || 0} month{(data?.rows?.length || 0) !== 1 ? "s" : ""} of data for {year}
                      </div>
                    </div>
                    <button
                      onClick={handleAnnualExport}
                      disabled={exporting}
                      style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", background: "#f4eefb", border: "1.5px solid #6f42c133", borderRadius: 10, color: "#6f42c1", fontWeight: 600, fontSize: 13, cursor: "pointer", opacity: exporting ? 0.6 : 1 }}
                    >
                      <FiDownload size={14} />
                      {exporting ? <Spinner size="sm" /> : "Export Annual CSV"}
                    </button>
                  </div>
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin"><Spin size="large" /></div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={columns}
                        dataSource={data?.rows || []}
                        rowKey={(r) => `${r.year}-${r.month}`}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">

                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                    <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>
                      TDS by User — {year}
                      {usersMonth ? ` · ${MONTH_OPTIONS.find((m) => m.value === usersMonth)?.label || ""}` : ""}
                    </h6>
                    <button
                      onClick={handleUsersExport}
                      disabled={exportingUsers || !users.length}
                      title={!users.length ? "No data to export" : "Export filtered user-level TDS as CSV"}
                      style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", background: "#1b5a90", border: "none", borderRadius: 10, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", opacity: (!users.length || exportingUsers) ? 0.6 : 1 }}
                    >
                      <FiDownload size={14} />
                      {exportingUsers ? <Spinner size="sm" /> : "Export CSV"}
                    </button>
                  </div>

                  {/* Filter toolbar */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 20 }}>
                    {/* Search */}
                    <div style={{ position: "relative", flex: "1 1 220px" }}>
                      <input
                        type="text"
                        placeholder="Search name, email, PAN, phone..."
                        value={usersSearch}
                        onChange={(e) => setUsersSearch(sanitizeInput(e.target.value))}
                        style={{ width: "100%", paddingLeft: 14, paddingRight: 36, paddingTop: 9, paddingBottom: 9, fontSize: 13, border: "1.5px solid #e9ecef", borderRadius: 10, background: "#fff", outline: "none", boxSizing: "border-box", color: "#374151" }}
                      />
                      <FiSearch size={14} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" }} />
                    </div>

                    {/* Month */}
                    <div style={{ position: "relative" }}>
                      <FiCalendar size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7280", pointerEvents: "none", zIndex: 1 }} />
                      <FiChevronDown size={12} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none", zIndex: 1 }} />
                      <select
                        value={usersMonth}
                        onChange={(e) => setUsersMonth(e.target.value === "" ? "" : Number(e.target.value))}
                        style={{ paddingLeft: 32, paddingRight: 28, paddingTop: 9, paddingBottom: 9, fontSize: 13, border: "1.5px solid #e9ecef", borderRadius: 10, background: "#fff", outline: "none", appearance: "none", cursor: "pointer", color: "#374151", minWidth: 140 }}
                      >
                        {MONTH_OPTIONS.map((m) => (
                          <option key={m.value || "all"} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Page size */}
                    <div style={{ position: "relative" }}>
                      <FiList size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7280", pointerEvents: "none", zIndex: 1 }} />
                      <FiChevronDown size={12} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none", zIndex: 1 }} />
                      <select
                        value={usersPageSize}
                        onChange={(e) => setUsersPageSize(Number(e.target.value))}
                        style={{ paddingLeft: 32, paddingRight: 28, paddingTop: 9, paddingBottom: 9, fontSize: 13, border: "1.5px solid #e9ecef", borderRadius: 10, background: "#fff", outline: "none", appearance: "none", cursor: "pointer", color: "#374151" }}
                      >
                        {[10, 25, 50, 100].map((n) => (
                          <option key={n} value={n}>{n} / page</option>
                        ))}
                      </select>
                    </div>

                    {/* Clear */}
                    {(usersSearch || usersMonth) && (
                      <button
                        type="button"
                        onClick={() => { setUsersSearch(""); setUsersMonth(""); }}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, border: "1.5px solid #fca5a5", borderRadius: 10, background: "#fff5f5", color: "#dc2626", cursor: "pointer" }}
                      >
                        <FiX size={13} /> Clear
                      </button>
                    )}

                    {/* User count */}
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 13 }}>
                      <FiUsers size={14} color="#1b5a90" />
                      <span style={{ fontWeight: 500 }}>
                        {usersLoading ? "Loading…" : `${usersTotal} user${usersTotal === 1 ? "" : "s"}`}
                      </span>
                    </div>
                  </div>

                  {/* Totals strip */}
                  {usersTotals && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
                      {[
                        { label: "Total TDS",      value: formatInr(usersTotals.totalTds || 0),          color: "#1b5a90", bg: "#eef4fb", Icon: FiFileText },
                        { label: "Total GST",      value: formatInr(usersTotals.totalGst || 0),          color: "#28a745", bg: "#eaf7ee", Icon: FiPercent },
                        { label: "Total Fee",      value: formatInr(usersTotals.totalFee || 0),          color: "#6f42c1", bg: "#f4eefb", Icon: FiCreditCard },
                        { label: "Trading TDS",    value: formatInr(usersTotals?.trading?.tds || 0),     color: "#fd7e14", bg: "#fff4e5", Icon: FiTrendingUp },
                        { label: "Withdrawal TDS", value: formatInr(usersTotals?.withdrawal?.tds || 0),  color: "#dc3545", bg: "#fdf0f1", Icon: FiBriefcase },
                      ].map(({ label, value, color, bg, Icon }) => (
                        <div key={label} style={{ flex: "1 1 150px", background: bg, border: `1px solid ${color}22`, borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Icon size={18} color={color} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 3 }}>{label}</div>
                            <div style={{ fontSize: 17, fontWeight: 800, color }}>{value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Table */}
                  <div className="table-responsive">
                    {usersLoading ? (
                      <div className="his-spin"><Spin size="large" /></div>
                    ) : (
                      <Table
                        pagination={{
                          current: usersPage,
                          pageSize: usersPageSize,
                          total: usersTotal,
                          onChange: (p) => setUsersPage(p),
                          showSizeChanger: false,
                        }}
                        style={{ overflowX: "auto" }}
                        columns={userColumns}
                        dataSource={users}
                        rowKey={(r) => r?.userId || r?.pan || r?.email}
                        locale={{
                          emptyText: (
                            <div style={{ padding: "32px 0", color: "#888" }}>
                              No user-level TDS data for {year}
                              {usersMonth ? ` / month ${usersMonth}` : ""}
                              {usersSearchDebounced ? ` matching "${usersSearchDebounced}"` : ""}
                            </div>
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel count={data?.rows?.length || 0} onClear={() => setYear(currentYear)}>
                <FilterPanel.Field label="Year">
                  <input
                    type="number"
                    min={2020}
                    max={2100}
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value) || currentYear)}
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TDSReports;
